import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { QueryKeys } from '../constants';
import { MissingIdError, useFetch } from './utils';

export function useThemes({ themeIds }, options = {}) {
  const fetcher = useFetch();
  return useQuery({
    queryKey: [QueryKeys.THEMES, { themeIds }],
    queryFn: () =>
      fetcher({
        path: `v1/themes/${themeIds}/`,
      }),
    ...options,
  });
}

export const useCreateUpdateThemes = (options = {}) => {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ codeFrameId, data }) => {
      if (!codeFrameId) {
        throw new MissingIdError('codeFrameId');
      }
      return fetcher({ path: 'v1/themes/update/', method: 'PUT', data: JSON.stringify(data) });
    },
    onSettled: (_data, _error, { codeFrameId }) => {
      queryClient.invalidateQueries([QueryKeys.SUGGESTED_THEMES, { codeFrameId }]);
      queryClient.invalidateQueries([QueryKeys.CODE_FRAME_THEMES, { codeFrameId }]);
      queryClient.invalidateQueries([QueryKeys.THEME_COVERAGE, { codeFrameId }]);
    },
    onError: () => {
      message.error('Something went wrong creating or updating themes');
    },
    ...options,
  });
};

export const useDeleteThemes = () => {
  const fetcher = useFetch();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ codeFrameId, themeIds }) => {
      if (!codeFrameId) {
        throw new MissingIdError('codeFrameId');
      }
      return fetcher({ path: `v1/themes/${themeIds.join()}/delete/`, method: 'DELETE' });
    },
    onSettled: (_data, _error, { codeFrameId }) => {
      queryClient.invalidateQueries([QueryKeys.SUGGESTED_THEMES, { codeFrameId }]);
      queryClient.invalidateQueries([QueryKeys.CODE_FRAME_THEMES, { codeFrameId }]);
      queryClient.invalidateQueries([QueryKeys.THEME_COVERAGE, { codeFrameId }]);
      queryClient.invalidateQueries([QueryKeys.CODE_FRAME_RESPONSES, { codeFrameId }]);
    },
    onError: () => {
      message.error('Something went wrong deleting themes');
    },
  });
};
